import { Location } from "history";

import store from "./store";

import * as NavigationUtils from "./lib/util/NavigationUtils";
import * as AuthUtils from "./lib/util/AuthUtils";
import * as SupportUtils from "./lib/util/SupportUtils";
import * as StringUtils from "./lib/util/StringUtils";
import * as Utils from "./lib/util/Utils";
import * as StorageUtils from "./lib/util/StorageUtils";
import * as GlobalActions from "./store/Global/Global.action";

import {
	Route,
	RouterConfig,
	RouteTemplate,
	RoutingMiddlewareResultType,
} from "./lib/util/NavigationUtils";
import { GLOBAL_FLEET_ID } from "./store/Fleet/Fleet.selector";
import { BackgroundOptions } from "./components/common/Background/Background";
import { AuthLevel } from "./lib/consts/AuthConstants";
import { isMultiFactorMandatedButNotEnabled } from "./lib/util/AuthUtils";
import { OVERRIDE_FLEET_KEY } from "./StorageKeys";

export interface BrightmileRouteOptions {
	noSupport?: boolean;
	noAuth?: boolean;
	noAuthOnly?: boolean;
	navigation?: boolean;
	disableGroupSelect?: boolean;
	requiresLevel?: number;
	background?: BackgroundOptions;
	noScrollToTop?: boolean;
	allowIE?: boolean;
	notification?: boolean;
	splashNotification?: boolean;
	delayChatLoad?: boolean;
	allowGlobalFleet?: boolean;
	blockNoTrends?: boolean;
}

const splashPageTemplate: RouteTemplate<BrightmileRouteOptions> = {
	options: {
		background: { disabled: true },
		noAuth: true,
		navigation: false,
	},
};

const routes = {
	homePage: {
		path: "/",
		template: splashPageTemplate,
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "HomePage" */ "./views/Login/Login"
			),
		options: { noAuthOnly: true },
	},
	multiFactorMandate: {
		path: "/multi-factor-mandate",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "MultiFactorMandate" */ "./views/MultiFactorMandate/MultiFactorMandate"
			),
		options: {
			navigation: false,
		},
	},
	login: {
		path: "/login",
		template: splashPageTemplate,
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "Login" */ "./views/Login/Login"
			),
		options: { noAuthOnly: true, noScrollToTop: true },
	},
	superLogin: {
		path: "/super-login",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "SuperLogin" */ "./views/SuperLogin/SuperLogin"
			),
		options: {
			noSupport: true,
			noAuth: true,
			navigation: false,
			noScrollToTop: true,
			notification: false,
			splashNotification: false,
		},
	},
	// groups: {
	//   path: "/groups",
	//   component: () =>
	//     import(
	//       /* eslint-disable-next-line */
	//       /* webpackChunkName: "Groups" */ "./views/Groups/Groups"
	//     ),
	// },
	compare: {
		path: "/compare",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "Compare" */ "./views/Compare/Compare"
			),
		options: { blockNoTrends: true },
	},
	dashboard: {
		path: "/home",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "Dashboard" */ "./views/Dashboard/Dashboard"
			),
		titleFormatter: (location: Location) => {
			if (location.hash) {
				return StringUtils.toTitleCase(location.hash.substr(1)) + " Dashboard";
			}
			return "Dashboard";
		},
		options: { blockNoTrends: true },
	},
	insights: {
		path: "/insights",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "InsightsIndex" */ "./views/InsightsIndex/InsightsIndex"
			),
		options: { blockNoTrends: true },
	},
	files: {
		path: "/files",
		title: "Files",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "Files" */ "./views/Files/FilesIndex"
			),
	},
	drivers: {
		path: "/drivers",
		title: "Drivers",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "DriverIndex" */ "./views/DriverIndex/DriverIndex"
			),
	},
	driverProfile: {
		path: "/driver/:uid",
		title: "Driver Profile",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "DriverProfile" */ "./views/DriverProfile/DriverProfile"
			),
		options: { disableGroupSelect: true },
	},
	forgotPassword: {
		path: "/forgot-password",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "ForgotPassword" */ "./views/ForgotPassword/ForgotPassword"
			),
		template: splashPageTemplate,
		options: {
			noAuthOnly: true,
			allowIE: true,
			notification: false,
			splashNotification: false,
		},
	},
	passwordReminder: {
		path: "/password-reminder",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "PasswordReminderSent" */ "./views/PasswordReminderSent/PasswordReminderSent"
			),
		template: splashPageTemplate,
		options: {
			noAuthOnly: true,
			allowIE: true,
			notification: false,
			splashNotification: false,
		},
	},
	profile: {
		path: "/profile",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "Profile" */ "./views/Profile/Profile"
			),
	},

	rewardsPreview: {
		path: "/support/rewards",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "RewardsPreview" */ "./views/RewardsPreview/RewardsPreview"
			),
		options: { requiresLevel: AuthLevel.SUPER_ADMIN },
	},
	support: {
		path: "/support",
		component: () =>
			import(/* webpackChunkName: "Support" */ "./views/Support/Support"),
		options: {
			disableGroupSelect: true,
			requiresLevel: AuthLevel.SUPER_ADMIN,
			allowGlobalFleet: true,
		},
	},
	rewardsBuilder: {
		path: "/support/rewards-builder",
		component: () =>
			import(
				/* webpackChunkName: "RewardsBuilder" */ "./views/RewardsBuilder/RewardsBuilder"
			),
		options: {
			requiresLevel: AuthLevel.SUPER_ADMIN,
		},
	},
	rewardsBuilderCompetition: {
		path: "/support/rewards-builder/:compId",
		component: () =>
			import(
				/* webpackChunkName: "RewardsBuilder" */ "./views/RewardsBuilder/RewardsBuilder"
			),
		options: {
			requiresLevel: AuthLevel.SUPER_ADMIN,
		},
	},
	rewards: {
		path: "/rewards",
		component: () =>
			import(/* webpackChunkName: "Rewards" */ "./views/Rewards/Rewards"),
	},
	rewardsResult: {
		path: "/rewards/:competitionId",
		component: () =>
			import(
				/* webpackChunkName: "Rewards" */ "./views/Rewards/sub-components/RewardsResult"
			),
		options: { disableGroupSelect: true },
	},
	reports: {
		path: "/reports",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "Reports" */ "./views/Reports/Reports"
			),
		options: { blockNoTrends: true },
	},
	compliancereport: {
		path: "/reports/compliance-report/:formId",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "Reports" */ "./views/Reports/ComplianceReport"
			),
	},
	vehiclecheckreport: {
		path: "/reports/vehicle-checks",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "Reports" */ "./views/Reports/VehicleCheckReport"
			),
	},
	grouprankingreport: {
		path: "/reports/group-ranking",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "Reports" */ "./views/Reports/GroupRankingReport"
			),
	},
	userrankingreport: {
		path: "/reports/user-ranking",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "Reports" */ "./views/Reports/UserRankingReport"
			),
	},
	brightcoachgrouprankingreport: {
		path: "/reports/brightcoach-group-ranking",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "Reports" */ "./views/Reports/BrightCoachGroupRankingReport"
			),
	},
	brightcoachuserrankingreport: {
		path: "/reports/brightcoach-user-ranking",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "Reports" */ "./views/Reports/BrightCoachUserRankingReport"
			),
	},
	leaderboards: {
		path: "/leaderboards",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "Leaderboards" */ "./views/Leaderboards/Leaderboards"
			),
	},
	leaderboardRankings: {
		path: "/leaderboard/:id",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "LeaderboardRankings" */ "./views/LeaderboardRankings/LeaderboardRankings"
			),
		titleFormatter: (location: Location) => "Leaderboard Rankings",
		options: { disableGroupSelect: true },
	},
	federatedSignUp: {
		path: "/federated-error",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "FederatedError" */ "./views/FederatedError/FederatedError"
			),
		options: {
			navigation: false,
			noAuth: true,
		},
	},

	privacy: {
		path: "/privacy",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "PortalPrivacy" */ "./views/Legal/PortalPrivacy"
			),
		options: {
			noSupport: true,
			noAuth: true,
			notification: false,
			splashNotification: false,
		},
	},
	privacyAndroid: {
		path: "/privacy/android",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "AndroidPrivacy" */ "./views/Legal/AppPrivacy"
			),
		options: {
			noSupport: true,
			noAuth: true,
			notification: false,
			splashNotification: false,
		},
	},
	termsUnileverIsrael: {
		path: "/terms/ul-he",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "AndroidTerms" */ "./views/Legal/UnileverIsraelTerms"
			),
		options: {
			noSupport: true,
			noAuth: true,
			notification: false,
			splashNotification: false,
		},
	},
	termsUnileverHungary: {
		path: "/terms/ul-hu",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "AndroidTerms" */ "./views/Legal/UnileverHungaryTerms"
			),
		options: {
			noSupport: true,
			noAuth: true,
			notification: false,
			splashNotification: false,
		},
	},
	termsGeotab: {
		path: "/terms/geotab",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "GeotabTerms" */ "./views/Legal/GeotabTerms"
			),
		options: {
			noSupport: true,
			noAuth: true,
			notification: false,
			splashNotification: false,
		},
	},
	termsAkzo: {
		path: "/terms/akzo-nobel",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "AkzoTerms" */ "./views/Legal/AkzoTerms"
			),
		options: {
			noSupport: true,
			noAuth: true,
			notification: false,
			splashNotification: false,
		},
	},
	termsDiageoTaiwan: {
		path: "/terms/diageo-tw",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "DiageoTwTerms" */ "./views/Legal/DiageoTaiwanTerms"
			),
		options: {
			noSupport: true,
			noAuth: true,
			notification: false,
			splashNotification: false,
		},
	},
	termsDiageoTaiwanEnglish: {
		path: "/terms/diageo-en",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "DiageoTwTerms" */ "./views/Legal/DiageoTaiwanTerms"
			),
		options: {
			noSupport: true,
			noAuth: true,
			notification: false,
			splashNotification: false,
		},
	},
	termsAndroid: {
		path: "/terms/android",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "AndroidTerms" */ "./views/Legal/AppTerms"
			),
		options: {
			noSupport: true,
			noAuth: true,
			notification: false,
			splashNotification: false,
		},
	},
	termsIos: {
		path: "/terms/ios",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "IosTerms" */ "./views/Legal/AppTerms"
			),
		options: {
			noSupport: true,
			noAuth: true,
			notification: false,
			splashNotification: false,
		},
	},
	privacyIos: {
		path: "/privacy/ios",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "IosPrivacy" */ "./views/Legal/AppPrivacy"
			),
		options: {
			noSupport: true,
			noAuth: true,
			notification: false,
			splashNotification: false,
		},
	},
	policyAndroid: {
		path: "/policies/android",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "AndroidPolicies" */ "./views/Legal/AppPolicies"
			),
		options: {
			noSupport: true,
			noAuth: true,
			notification: false,
			splashNotification: false,
		},
	},
	policyIos: {
		path: "/policies/ios",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "IosPolicies" */ "./views/Legal/AppPolicies"
			),
		options: {
			noSupport: true,
			noAuth: true,
			notification: false,
			splashNotification: false,
		},
	},

	authAction: {
		path: "/__/action",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "PageAuthAction" */ "./views/PageAuthAction/PageAuthAction"
			),
		template: splashPageTemplate,
		options: {
			noSupport: true,
			allowIE: true,
			notification: false,
			splashNotification: false,
		},
	},
	userGuide: {
		path: "/user-guide",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "UserGuide" */ "./views/UserGuide/UserGuide"
			),
		template: splashPageTemplate,
		options: {
			noSupport: true,
			notification: false,
			splashNotification: false,
		},
	},
	appSupport: {
		path: "/app-support",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "AppSupport" */ "./views/AppSupport/AppSupport"
			),
		options: {
			noAuth: true,
			noScrollToTop: true,
			navigation: false,
			notification: false,
			splashNotification: false,
			delayChatLoad: true,
		},
	},
	unsupportedBrowser: {
		path: "/unsupported-browser",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "AppSupport" */ "./views/UnsupportedBrowser/UnsupportedBrowser"
			),
		options: {
			noAuth: true,
			noScrollToTop: true,
			navigation: false,
			allowIE: true,
			notification: false,
			splashNotification: false,
		},
	},

	error404: {
		path: "*",
		component: () =>
			import(
				/* eslint-disable-next-line */
				/* webpackChunkName: "Error404" */ "./views/Error404/Error404"
			),
		title: "Page Not Found",
		template: splashPageTemplate,
	},
};

const routerConfig: RouterConfig<keyof typeof routes, BrightmileRouteOptions> =
	{
		name: "Brightmile Portal",
		middleware: [
			(route) => {
				const isIE = Utils.isIE();

				if (isIE && route && !route.options?.allowIE) {
					return {
						type: RoutingMiddlewareResultType.Redirect,
						location: "/unsupported-browser",
					};
				} else if (!isIE && route?.path === "/unsupported-browser") {
					return {
						type: RoutingMiddlewareResultType.Redirect,
						location: "/home",
					};
				}

				const isLoggedIn = AuthUtils.isUserLoggedIn();

				if (isLoggedIn && route?.options?.noAuthOnly) {
					return {
						type: RoutingMiddlewareResultType.Redirect,
						location: "/home",
					};
				}

				if (
					route?.options?.requiresLevel &&
					!AuthUtils.isAuthLevelMet(route?.options?.requiresLevel)
				) {
					return {
						type: RoutingMiddlewareResultType.Redirect,
						location: isLoggedIn ? "/home" : "/",
						includeRedirect: true,
					};
				}

				if (!route?.options?.delayChatLoad) {
					SupportUtils.loadSupportWidget();
				}

				const requiresAuth = route && isAuthRequiredForRoute(route);

				// User is not authorised - redirect to home page
				if (requiresAuth && !isLoggedIn) {
					SupportUtils.toggleSupport(true);
					return {
						type: RoutingMiddlewareResultType.Redirect,
						location: "/",
						includeRedirect: true,
					};
				}

				return RoutingMiddlewareResultType.Continue;
			},
			(route) => {
				if (route) {
					const noSupport = route.options?.noSupport;
					SupportUtils.toggleSupport(noSupport || false);
				}
				return RoutingMiddlewareResultType.Continue;
			},
			(route) => {
				if (
					isMultiFactorMandatedButNotEnabled() &&
					isAuthRequiredForRoute(route) &&
					route?.id !== "multiFactorMandate"
				) {
					return {
						type: RoutingMiddlewareResultType.Redirect,
						location: "/multi-factor-mandate",
						includeRedirect: true,
					};
				}

				return RoutingMiddlewareResultType.Continue;
			},
			(route) => {
				if (!route?.options?.allowGlobalFleet) {
					const fleetIdOverride = StorageUtils.get(OVERRIDE_FLEET_KEY);

					if (fleetIdOverride === GLOBAL_FLEET_ID) {
						const loggedInUser = AuthUtils.getStoredUserObject();

						store?.dispatch(
							GlobalActions.setScope({
								fleetId: loggedInUser?.fleetId,
								group: loggedInUser?.group,
							}),
						);
					}
				}

				return RoutingMiddlewareResultType.Continue;
			},
		],
		routes,
	};

export const isAuthRequiredForRoute = (
	route: Route<BrightmileRouteOptions> | null,
) => {
	return route && !route.options?.noAuthOnly && !route.options?.noAuth;
};

export default NavigationUtils.createRouterManager(routerConfig);
